import React from 'react';
import dayjs from 'dayjs';
import { Box, Typography, Button, Divider } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import employeeSalaryReportPDF from '../PDF/employeeSalaryReportPDF';
import CalSSO from '../../utils/functions/CalSSO';
import WindowFrame from './WindowFrame';
import {
  calcOtForDay,
  calcCheckinDisplaySalary,
  calcCheckinActualSalary,
} from '../Table/payroll/utils';

const MONTH_TH = [
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
];
const DAY_TH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
const LEAVE_ABBR = {
  SICK: 'ป่วย',
  PERSONAL: 'กิจ',
  VACATION: 'พร.',
  MATERNITY: 'คลอด',
  ORDINATION: 'บวช',
  UNPAID: 'ไม่รับ',
};

const fmt = (n) =>
  Number(n || 0).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtH = (h) => (Number.isInteger(h) ? h : Number(h).toFixed(2));

const getActualLateMinutes = (checkInTime) => {
  if (!checkInTime) return 0;
  const [h, m] = checkInTime.split(':').map(Number);
  if (h >= 12) {
    // กะบ่าย: เริ่ม 13:00 — ไม่สายถ้าเข้า ≤ 13:00
    return Math.max(0, h * 60 + m - 13 * 60);
  }
  return Math.max(0, h * 60 + m - 8 * 60);
};

const calcDayLateDeduction = (checkInTime, attendanceRules, spd, spm) => {
  const minutesLate = getActualLateMinutes(checkInTime);
  if (minutesLate <= 0) return 0;
  const lateRule = (attendanceRules || []).find(
    (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
  );
  let effectiveMin;
  if (!lateRule) {
    effectiveMin = minutesLate;
  } else {
    if (minutesLate < lateRule.threshold_minutes) return 0;
    effectiveMin = Math.max(minutesLate, lateRule.deduct_minutes);
  }
  const rate = spm > 0 ? spm / 30 / 8 / 60 : spd / 8 / 60;
  return Number((effectiveMin * rate).toFixed(2));
};

const cellSt = (bold, color, align = 'right') => ({
  padding: '5px 8px',
  fontSize: '0.78rem',
  fontWeight: bold ? 700 : 400,
  color: color || 'inherit',
  textAlign: align,
  borderBottom: '1px solid #f1f5f9',
});
const headSt = (align = 'right') => ({
  padding: '6px 8px',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: '#64748b',
  textAlign: align,
  background: '#f8fafc',
  borderBottom: '2px solid #e2e8f0',
});

export default function EmployeeSalaryReport({
  open,
  onClose,
  emp,
  month,
  year,
  manualWorkData = [],
  leaveData = [],
  payrollRow,
  attendanceRules = [],
  days: filterDays,
  selectedOption = 'Month',
  precomputedRows,
  // พนักงานรายเดือน (โหมดชม.จริง): จำนวนวันที่ต้องปรับให้ครบ 30 วัน (+ วัน/− วัน) แสดงเป็นแถวท้ายตาราง
  fulltimeAdjustDays = 0,
  salaryMode = 'late-deduct',
  onProjectClick,
  onResetAll,
  // พนักงานรายเดือน: รายละเอียดค่าแรงที่แบ่งลงแต่ละโครงการตาม % ชั่วโมง (fulltimeProjectSplit จาก
  // MergedPayrollTableV2.js projectDashboard) — undefined = ไม่โชว์ส่วนนี้ (รายวัน/งวดที่บันทึกแล้ว/หน้า V1)
  projectSplit,
}) {
  if (!emp) return null;

  const spd = emp.salary_per_day || 0;
  const spm = emp.salary_per_month || 0;
  const isFulltime = emp.emp_type === 'FULLTIME';
  const daysInMonth = dayjs(`${year}-${month + 1}`).daysInMonth();

  // ช่วงวันที่ตาม filter
  const activeDays =
    filterDays && filterDays.length > 0
      ? filterDays
      : Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const startDay = activeDays[0];
  const endDay = activeDays[activeDays.length - 1];
  const periodLabel = `${startDay} ${MONTH_TH[month]} ${
    year + 543
  } – ${endDay} ${MONTH_TH[month]} ${year + 543}`;

  const manuals = (manualWorkData || []).filter(
    (m) => String(m.employee_id) === String(emp.employee_id),
  );
  const leaves = (leaveData || []).filter(
    (l) => String(l.employee?._id || l.employee_id) === String(emp.employee_id),
  );

  const checkinByDay = {};
  (emp.checkins || []).forEach((c) => {
    checkinByDay[dayjs(c.checkIn).date()] = c;
  });

  const manualByDay = {};
  manuals.forEach((m) => {
    manualByDay[dayjs(m.date).date()] = m;
  });

  const leaveByDay = {};
  for (let d = 1; d <= daysInMonth; d++) {
    const target = dayjs(`${year}-${month + 1}-${d}`);
    const lv = leaves.find((l) => {
      const s = dayjs(l.start_date).startOf('day');
      const e = dayjs(l.end_date).endOf('day');
      return !target.isBefore(s) && !target.isAfter(e);
    });
    if (lv) leaveByDay[d] = lv;
  }

  const getLeaveAutoH = (d, checkin) => {
    const lv = leaveByDay[d];
    if (!lv || emp.emp_type !== 'FULLTIME') return 0;
    const ch = checkin ? dayjs(checkin.checkIn).hour() : null;
    if (lv.period === 'FULL_DAY' && !checkin) return 8;
    if (lv.period === 'MORNING' && (!checkin || ch >= 12)) return 4;
    if (lv.period === 'AFTERNOON' && (!checkin || ch < 12)) return 4;
    return 0;
  };

  // สร้าง dailyRows — ใช้สูตรเดียวกับ buildSalaryContent + buildOtContent ใน MergedPayrollTable
  const dailyRows = [];
  for (const d of activeDays) {
    const checkin = checkinByDay[d];
    const manual = manualByDay[d];
    const leave = leaveByDay[d];
    const leaveAutoH = getLeaveAutoH(d, checkin);
    const date = dayjs(`${year}-${month + 1}-${d}`);
    const dow = date.day();
    const isSun = dow === 0;
    const isSat = dow === 6;

    // manual ที่นับได้: ต้องไม่มี timestamp_ref (เหมือน buildSalaryContent popup)
    const validManual = manual && !manual.timestamp_ref ? manual : null;

    if (!checkin && !validManual && leaveAutoH === 0) {
      dailyRows.push({
        d,
        dow,
        date,
        checkInTime: null,
        checkOutTime: null,
        hours: 0,
        wage: 0,
        actualH: 0,
        actualWage: 0,
        allowance: 0,
        notes: [],
        er: 1,
        otHours: 0,
        otRate: 1.5,
        otAmt: 0,
        lateDed: 0,
        leaveAutoH: 0,
        isSun,
        isSat,
        isManual: false,
        isLeaveOnly: false,
        isEmpty: true,
        project: null,
      });
      continue;
    }

    const er = checkin
      ? checkin.is_manual
        ? 1
        : checkin.effectiveRate || checkin.rate || 1
      : validManual?.work_rate || 1;

    let hours = 0,
      wage = 0,
      origDayWage = null,
      actualH = 0,
      actualWage = 0,
      notes = [];

    // ── ค่าแรงจาก checkin (calcCheckinDisplaySalary/calcCheckinActualSalary — ตรงกับตาราง + popup) ──
    // ฟังก์ชันนี้จัดการ leave แยกต่างหากด้านล่าง (leaveAutoH) จึงส่ง lv=null เสมอ
    if (checkin) {
      const hasSalaryOverride = checkin.salary_override?.amount != null;
      const actualR = calcCheckinActualSalary(checkin, null, spd);
      actualH = actualR.hours;
      actualWage = actualR.amt;
      const displayR = calcCheckinDisplaySalary(checkin, null, spd);
      wage = displayR.amt;
      hours = displayR.hours;
      if (hasSalaryOverride) {
        origDayWage = displayR.defaultAmt;
        notes.push({
          text: `แก้ค่าแรงโดย: ${
            checkin.salary_override.edited_by || 'HR'
          } (×${displayR.rate} × ฿${fmt(displayR.dayRate)}/วัน)`,
          type: 'wage',
        });
      } else {
        const citH = checkin.checkInTime
          ? parseInt(checkin.checkInTime.split(':')[0], 10)
          : 0;
        const isAfternoon = citH >= 12;
        if (er > 1) notes.push({ text: `×${er} วันหยุด`, type: 'holiday' });
        if (isAfternoon)
          notes.push({ text: 'ลงเวลาช่วงบ่าย', type: 'afternoon' });
      }
      if (
        checkin.deductionReasons?.includes('late') &&
        !checkin.skip_late_deduct
      )
        notes.push({ text: 'สาย ส', type: 'late' });
      if (checkin.deductionReasons?.includes('no_checkout'))
        notes.push({ text: 'ไม่เช็คเอาท์ อ', type: 'nocheckout' });
      if (checkin.skip_late_deduct)
        notes.push({ text: 'ยกเว้นหักสาย', type: 'skip_late' });
    }

    // ── ค่าแรงจาก manual (เฉพาะที่ไม่มี timestamp_ref — เหมือน popup) ──
    if (validManual) {
      const mRate = validManual.work_rate || 1;
      const mWage =
        validManual.amount != null
          ? Number(validManual.amount)
          : (validManual.hours / 8) * spd * mRate;
      const rateLabel = mRate > 1 ? ` (×${mRate} วันหยุด)` : '';
      if (!checkin) {
        hours = validManual.hours || 0;
        wage = mWage;
        actualH = validManual.hours || 0;
        actualWage = mWage;
        notes.push({
          text: `เพิ่มชั่วโมง ${validManual.hours} ชม.${rateLabel} ค่าแรง ${fmt(
            mWage,
          )} บาท โดย ${validManual.edited_by || 'HR'}`,
          type: 'hour',
        });
      } else {
        // บวกเพิ่มบน checkin (HR เพิ่มชั่วโมงพิเศษ)
        wage += mWage;
        actualWage += mWage;
        notes.push({
          text: `+เพิ่มชั่วโมง ${validManual.hours} ชม.${rateLabel} +${fmt(
            mWage,
          )} บาท โดย ${validManual.edited_by || 'HR'}`,
          type: 'hour',
        });
      }
    }

    // ── ลา FULLTIME ──
    if (leaveAutoH > 0) {
      const leaveWage = (leaveAutoH / 8) * spd;
      const leaveName =
        LEAVE_ABBR[leave?.leave_type] || leave?.leave_type || 'ลา';
      const periodLabel2 =
        leave?.period === 'FULL_DAY'
          ? 'เต็มวัน'
          : leave?.period === 'MORNING'
          ? 'ครึ่งวันเช้า'
          : 'ครึ่งวันบ่าย';
      hours += leaveAutoH;
      wage += leaveWage;
      actualH += leaveAutoH;
      actualWage += leaveWage;
      notes.push({
        text: `ลา${leaveName} (${periodLabel2}) +${leaveAutoH} ชม. = +${fmt(
          leaveWage,
        )} บาท`,
        type: 'leave',
      });
    }

    // ── OT (calcOtForDay จาก payroll/utils.js — ใช้สูตรเดียวกับ MergedPayrollTableV2) ──
    // dayHrR ยังคำนวณแยกที่นี่ (ใช้เฉพาะตอนไม่มี precomputedRows จากตาราง)
    const dayHrR = (() => {
      if (!checkin) return spd / 8;
      if (checkin.salary_override?.amount != null)
        return checkin.salary_override.amount / 8;
      const er2 = checkin.effectiveRate || checkin.rate || 1;
      const citH2 = checkin.checkInTime
        ? parseInt(checkin.checkInTime.split(':')[0], 10)
        : 0;
      if (citH2 >= 12) return (er2 * spd) / 8;
      const dH = Math.max(checkin.totalHour ?? 8, 8);
      return ((dH / 8) * er2 * spd) / 8;
    })();

    const { otHours, otRate, otAmt, isManualOt } = calcOtForDay(
      checkin,
      manual,
      dayHrR,
    );

    const allowance = checkin?.extra_day || manual?.allowance_amount || 0;
    const lateDed =
      checkin?.skip_late_deduct || !checkin?.checkInTime
        ? 0
        : calcDayLateDeduction(checkin.checkInTime, attendanceRules, spd, spm);

    dailyRows.push({
      d,
      dow,
      date,
      checkInTime: checkin?.checkInTime || null,
      checkOutTime: checkin?.checkOutTime || null,
      project: checkin?.project_name
        ? `${checkin.project_name}${
            checkin.project_number ? ` (${checkin.project_number})` : ''
          }`
        : null,
      projectNumber: checkin?.project_number || null,
      projectId: checkin?.project_id ? String(checkin.project_id) : null,
      hours,
      wage,
      origDayWage,
      actualH,
      actualWage,
      allowance,
      notes,
      er,
      otHours,
      otRate,
      otAmt,
      isManualOt,
      lateDed,
      leaveAutoH,
      leave,
      isSun,
      isSat,
      isManual: !checkin && !!validManual,
      isLeaveOnly: !checkin && !validManual && leaveAutoH > 0,
      isEmpty: false,
      isHourForced: checkin?.hour_override?.hours === 8,
      rawHoursBeforeForce: checkin?.hour_override?.previous_hours ?? null,
    });
  }

  // ถ้า MergedPayrollTable ส่ง precomputedRows มา ใช้เลย — ไม่ต้องคำนวณซ้ำ
  const rows = precomputedRows || dailyRows;

  const totalWage = rows.reduce((s, r) => s + Number((r.wage || 0).toFixed(2)), 0);
  const totalActualWage = rows.reduce((s, r) => s + Number((r.actualWage || 0).toFixed(2)), 0);
  const totalOt = rows.reduce((s, r) => s + Number((r.otAmt || 0).toFixed(2)), 0);
  const totalLateDed = rows.reduce((s, r) => s + Number((r.lateDed || 0).toFixed(2)), 0);
  const totalAllowanceDaily = rows.reduce((s, r) => s + Number((r.allowance || 0).toFixed(2)), 0);
  const totalOtHours = rows.reduce((s, r) => s + r.otHours, 0);
  const totalOtEffHours = rows.reduce((s, r) => s + r.otHours * (r.otRate || 1.5), 0);
  const workDaysCount = rows.filter((r) => !r.isEmpty).length;
  // SSO: form → emp → คำนวณจาก salary โดยตรง (fallback เมื่อ store ไม่มีค่า)
  const ssoSalaryBase = isFulltime && spm > 0 ? spm : totalWage;
  const ssoTax = payrollRow?.sso_tax ?? emp?.sso_tax;
  const ssoPaidVal = payrollRow?.sso_paid ?? emp?.sso_paid;
  const ssoDed =
    parseFloat(payrollRow?.expenses?.sso ?? 0) ||
    parseFloat(emp?.expenses?.sso ?? 0) ||
    parseFloat(CalSSO(ssoSalaryBase, ssoTax, ssoPaidVal) || 0);
  const taxDed =
    parseFloat(payrollRow?.expenses?.tax ?? 0) ||
    parseFloat(emp?.expenses?.tax ?? 0);
  const otherDed =
    parseFloat(payrollRow?.expenses?.other ?? 0) ||
    parseFloat(emp?.expenses?.other ?? 0);
  const allowances = parseFloat(payrollRow?.revenue?.allowances ?? 0);
  const bonus = parseFloat(payrollRow?.revenue?.bonus ?? 0);
  const fieldAllowanceTotal = rows.reduce(
    (s, r) => s + Number((r.fieldAllowance || 0).toFixed(2)),
    0,
  );

  const isWorkTime = salaryMode === 'work-time';
  // คอลัมน์ "โดนหัก" (ค่าแรง/วัน − ค่าแรงที่ได้ เพราะทำงานไม่ครบ 8 ชม.) — ตอนนี้ทำแค่พนักงานรายวัน
  // (isFulltime===false) ก่อนตามที่ขอ ยังไม่ทำให้พนักงานรายเดือน/โหมด late-deduct
  const showDeductCol = isWorkTime && !isFulltime;
  // workDaysEffCount = จำนวนวันทำงานถ่วงน้ำหนักด้วย rate (วันหยุด/อาทิตย์ = ×2) — logic เดียวกับ
  // displayCheck/displayCheckEff ของคอลัมน์ "วันทำงาน/OT ชม." ใน MergedPayrollTableV2
  const workDaysEffCount = rows.reduce(
    (s, r) => s + (r.isEmpty ? 0 : r.er || 1),
    0,
  );

  // ใช้ค่าจาก form (payrollRow) เป็น source of truth — ตรงกับตารางเสมอ
  // fallback ไป dailyRows ถ้าไม่มี form (เช่นเปิดจากที่อื่น)
  const summaryWage =
    payrollRow?.revenue?.salary != null
      ? parseFloat(payrollRow.revenue.salary)
      : isFulltime && spm > 0
      ? spm
      : isWorkTime
      ? totalActualWage
      : totalWage;
  const summaryOt =
    payrollRow?.revenue?.overtime != null
      ? parseFloat(payrollRow.revenue.overtime)
      : totalOt;
  const summaryLateDed = isWorkTime
    ? 0
    : payrollRow?.expenses?.late != null
    ? parseFloat(payrollRow.expenses.late)
    : totalLateDed;
  const summaryFieldAllowance =
    payrollRow?.revenue?.field_allowance != null
      ? parseFloat(payrollRow.revenue.field_allowance)
      : fieldAllowanceTotal;
  const net =
    (isFulltime && spm > 0 ? summaryWage : isWorkTime ? totalActualWage : summaryWage) +
    summaryOt +
    allowances +
    summaryFieldAllowance +
    bonus -
    summaryLateDed -
    ssoDed -
    taxDed -
    otherDed;

  const manualFields = payrollRow?.manual_fields || [];
  const computedWage = isFulltime && spm > 0 ? spm : isWorkTime ? totalActualWage : totalWage;

  const noteColor = (type) => {
    switch (type) {
      case 'late':
        return '#f97316';
      case 'nocheckout':
        return '#ef4444';
      case 'hour':
      case 'hour_wage':
        return '#3b82f6';
      case 'wage':
        return '#b45309';
      case 'holiday':
      case 'leave':
        return '#15803d';
      case 'skip_late':
        return '#7c3aed';
      case 'afternoon':
        return '#0284c7';
      default:
        return '#94a3b8';
    }
  };

  const pdfParams = {
    emp,
    month,
    year,
    dailyRows: rows,
    totalWage: summaryWage,
    totalOt: summaryOt,
    totalOtRaw: totalOt,
    totalLateDed: summaryLateDed,
    totalAllowanceDaily,
    totalOtHours,
    workDaysCount,
    ssoDed,
    taxDed,
    otherDed,
    allowances,
    fieldAllowanceTotal: summaryFieldAllowance,
    fieldAllowanceRaw: fieldAllowanceTotal,
    bonus,
    net,
    periodLabel,
    isWorkTime,
    totalActualWage,
    manualFields,
  };

  const handleOpen = () =>
    employeeSalaryReportPDF({ ...pdfParams, mode: 'open' });
  const handleDownload = () =>
    employeeSalaryReportPDF({ ...pdfParams, mode: 'download' });

  const SectionTitle = ({ children }) => (
    <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e293b', marginTop: 16, marginBottom: 6 }}>
      {children}
    </div>
  );

  // ส่วน "แบ่งค่าแรงลงโครงการ" — เรียงโครงการที่ % มากสุดขึ้นก่อน
  const splitRows = Object.entries(projectSplit?.rows || {})
    .map(([key, r]) => ({ key, ...r }))
    .sort((a, b) => b.share - a.share);
  const splitTotal = (field) => splitRows.reduce((s, r) => s + (Number(r[field]) || 0), 0);
  const hasSplitAdjust = splitRows.some((r) => Math.abs(r.adjust || 0) > 0.005);

  if (!open) return null;
  return (
    <WindowFrame
      open={open}
      onClose={onClose}
      title="รายงานค่าแรง"
      subtitle={`${emp.name}${emp.emp_code ? ` (${emp.emp_code})` : ''} — ${MONTH_TH[month]} ${year}`}
      taskbarLabel={`รายงานค่าแรง — ${emp.name}`}
    >
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', marginBottom: 4 }}>รายงานค่าแรง</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {emp.name} {emp.emp_code ? `(${emp.emp_code})` : ''} — {MONTH_TH[month]} {year}
          </div>
          {(emp.role || emp.department) && (
            <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{[emp.role, emp.department].filter(Boolean).join(' | ')}</div>
          )}
          <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>งวด: {periodLabel}</div>
        </div>

      {/* ════════════════════════════════════════════════════════
          MODAL DISPLAY — เหมือนเดิม (ไม่ใช้ printRef)
          ════════════════════════════════════════════════════════ */}
        {/* Content */}
        <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
        {/* ── ค่าแรงรายวัน ── */}
        <SectionTitle>📋 ค่าแรงรายวัน</SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              minWidth: isWorkTime ? 880 : 960,
            }}
          >
            <thead>
              <tr>
                {[
                  'วันที่',
                  'เข้า',
                  'ออก',
                  'โครงการ',
                  'ชม.',
                  'อัตรา',
                  ...(isWorkTime ? ['ค่าแรง/วัน'] : []),
                  isWorkTime ? 'ค่าแรงที่ได้' : 'ค่าแรง',
                  ...(showDeductCol ? ['โดนหัก'] : []),
                  'OT ชม.',
                  'ค่า OT',
                  'เบี้ยเลี้ยง',
                  'ออกสนาม',
                  ...(isWorkTime ? [] : ['หักสาย']),
                  'รวม',
                  'หมายเหตุ',
                ].map((h, i, arr) => (
                  <th
                    key={h}
                    style={headSt(
                      i === 0 || i === 3 || i === arr.length - 1
                        ? 'left'
                        : 'right',
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const dateColor = r.isSun
                  ? '#ea580c'
                  : r.isSat
                  ? '#dc2626'
                  : '#374151';
                const rowBg = r.isEmpty
                  ? r.isSun
                    ? '#fff7ed'
                    : r.isSat
                    ? '#fff5f5'
                    : '#fafafa'
                  : r.isLeaveOnly
                  ? '#f0fdf4'
                  : r.isSun
                  ? '#fff7ed'
                  : r.isSat
                  ? '#fff5f5'
                  : r.isManual
                  ? '#eff6ff'
                  : 'transparent';
                const dispH = isWorkTime ? r.actualH || 0 : r.hours;
                const dispWage = isWorkTime ? r.actualWage || 0 : r.wage;
                const dispTotal = isWorkTime
                  ? dispWage + r.otAmt + r.allowance + (r.fieldAllowance || 0)
                  : r.wage + r.otAmt + r.allowance + (r.fieldAllowance || 0) - r.lateDed;
                const deductAmt = showDeductCol
                  ? Number((r.wage - dispWage).toFixed(2))
                  : 0;
                return (
                  <tr key={r.d} style={{ background: rowBg }}>
                    <td style={cellSt(false, dateColor, 'left')}>
                      {DAY_TH[r.dow]} {r.d}/{month + 1}
                    </td>
                    {r.isEmpty && !(r.ftFill && isWorkTime) ? (
                      <td
                        style={cellSt(false, '#e2e8f0')}
                        colSpan={showDeductCol ? 14 : 13}
                      />
                    ) : (
                      <>
                        <td style={cellSt(false, '#374151')}>
                          {r.checkInTime || '—'}
                        </td>
                        <td style={cellSt(false, '#374151')}>
                          {r.checkOutTime || '—'}
                        </td>
                        <td
                          style={{
                            ...cellSt(false, '#374151', 'left'),
                            cursor: onProjectClick && r.projectId ? 'pointer' : 'default',
                            textDecoration: onProjectClick && r.projectId ? 'underline' : 'none',
                          }}
                          onClick={
                            onProjectClick && r.projectId
                              ? () => onProjectClick(r.projectId)
                              : undefined
                          }
                        >
                          {r.projectNumber || '—'}
                        </td>
                        <td style={cellSt(false, '#374151')}>
                          {r.isHourForced && r.rawHoursBeforeForce != null && (
                            <span
                              style={{
                                textDecoration: 'line-through',
                                opacity: 0.5,
                                fontSize: '0.75em',
                                marginRight: 3,
                              }}
                            >
                              {fmtH(r.rawHoursBeforeForce)}
                            </span>
                          )}
                          {fmtH(dispH)}
                        </td>
                        <td
                          style={cellSt(
                            false,
                            r.er > 1 ? '#16a34a' : '#374151',
                          )}
                        >
                          {r.er > 1 ? `×${r.er}` : '×1'}
                        </td>
                        {isWorkTime && (
                          <td style={cellSt(false, '#64748b')}>
                            {r.origDayWage != null &&
                            Math.abs(r.origDayWage - r.wage) > 0.01 ? (
                              <>
                                <span
                                  style={{
                                    textDecoration: 'line-through',
                                    opacity: 0.6,
                                    marginRight: 4,
                                  }}
                                >
                                  {fmt(r.origDayWage)}
                                </span>
                                {fmt(r.wage)}
                              </>
                            ) : (
                              fmt(r.wage)
                            )}
                          </td>
                        )}
                        <td style={cellSt(true, '#1e293b')}>{fmt(dispWage)}</td>
                        {showDeductCol && (
                          <td style={cellSt(true, deductAmt > 0 ? '#dc2626' : '#d1d5db')}>
                            {deductAmt > 0 ? `−${fmt(deductAmt)}` : '—'}
                          </td>
                        )}
                        <td
                          style={cellSt(
                            false,
                            r.otHours > 0 ? '#ea580c' : '#d1d5db',
                          )}
                        >
                          {r.otHours > 0 ? (
                            <>
                              {fmtH(r.otHours)} ชม.{' '}
                              <span style={{ fontSize: '0.62rem', color: '#9ca3af' }}>
                                (×{r.otRate})
                              </span>
                              {r.isManualOt && (
                                <span
                                  style={{
                                    fontSize: '0.62rem',
                                    color: '#9ca3af',
                                    marginLeft: 2,
                                  }}
                                >
                                  ✎
                                </span>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td
                          style={cellSt(
                            true,
                            r.otAmt > 0 ? '#ea580c' : '#d1d5db',
                          )}
                        >
                          {r.otAmt > 0 ? fmt(r.otAmt) : '—'}
                        </td>
                        <td
                          style={cellSt(
                            false,
                            r.allowance > 0 ? '#0369a1' : '#cbd5e1',
                          )}
                        >
                          {r.allowance > 0 ? fmt(r.allowance) : '—'}
                        </td>
                        <td
                          style={cellSt(
                            false,
                            r.fieldAllowance > 0 ? '#0369a1' : '#cbd5e1',
                          )}
                        >
                          {r.fieldAllowance > 0 ? fmt(r.fieldAllowance) : '—'}
                        </td>
                        {!isWorkTime && (
                          <td
                            style={cellSt(
                              true,
                              r.lateDed > 0 ? '#f97316' : '#d1d5db',
                            )}
                          >
                            {r.lateDed > 0 ? `−${fmt(r.lateDed)}` : '—'}
                          </td>
                        )}
                        <td style={cellSt(true, '#065f46')}>
                          {fmt(dispTotal)}
                        </td>
                        <td style={cellSt(false, '#94a3b8', 'left')}>
                          {r.notes.map((n, ni) => (
                            <span
                              key={ni}
                              style={{
                                display: 'block',
                                color: noteColor(n.type),
                                fontSize: '0.68rem',
                                lineHeight: 1.4,
                              }}
                            >
                              {n.text}
                            </span>
                          ))}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
              {/* รายเดือน: ปรับให้ครบ 30 วัน (เดือน 31 วัน = ลบ 1 วัน, เดือนที่น้อยกว่า 30 วัน = บวกอีก N วัน) */}
              {isFulltime && isWorkTime && fulltimeAdjustDays !== 0 && rows.length > 0 && (() => {
                const adjAmt = fulltimeAdjustDays * spd;
                const c = fulltimeAdjustDays > 0 ? '#065f46' : '#b91c1c';
                return (
                  <tr style={{ background: '#fffbeb' }}>
                    <td style={cellSt(false, c, 'left')} colSpan={7}>
                      {fulltimeAdjustDays > 0
                        ? `บวกอีก ${fulltimeAdjustDays} วัน × ฿${spd.toLocaleString('th-TH')} (ให้ครบ 30 วัน)`
                        : `ลบ ${-fulltimeAdjustDays} วัน × ฿${spd.toLocaleString('th-TH')} (ให้เหลือ 30 วัน)`}
                    </td>
                    <td style={cellSt(true, c)}>
                      {fulltimeAdjustDays > 0 ? '+' : ''}{fmt(adjAmt)}
                    </td>
                    <td style={cellSt(false, '#e2e8f0')} colSpan={4} />
                    <td style={cellSt(true, c)}>
                      {fulltimeAdjustDays > 0 ? '+' : ''}{fmt(adjAmt)}
                    </td>
                    <td style={cellSt(false, '#94a3b8', 'left')} />
                  </tr>
                );
              })()}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={showDeductCol ? 14 : 13}
                    style={{
                      ...cellSt(false, '#94a3b8', 'center'),
                      padding: 16,
                    }}
                  >
                    ไม่มีข้อมูลการทำงาน
                  </td>
                </tr>
              )}
              {/* footer row — ใช้ค่าจาก form (summaryWage/summaryOt/summaryLateDed) ให้ตรงกับตาราง */}
              {rows.length > 0 && (
                <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                  <td
                    style={{
                      ...cellSt(true, '#334155', 'left'),
                      borderTop: '2px solid #cbd5e1',
                    }}
                    colSpan={4}
                  >
                    รวม
                  </td>
                  <td
                    style={{
                      ...cellSt(true, '#1e293b'),
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    {Number.isInteger(workDaysCount)
                      ? workDaysCount
                      : workDaysCount.toFixed(1)}
                    {Math.abs(workDaysEffCount - workDaysCount) > 0.01 && (
                      <span style={{ fontSize: '0.62rem', color: '#9ca3af' }}>
                        {' '}
                        ({workDaysEffCount.toFixed(1)})
                      </span>
                    )}
                  </td>
                  <td
                    style={{
                      ...cellSt(false, '#94a3b8'),
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    —
                  </td>
                  {isWorkTime && (
                    <td
                      style={{
                        ...cellSt(false, '#64748b'),
                        borderTop: '2px solid #cbd5e1',
                      }}
                    >
                      {fmt(totalWage)}
                    </td>
                  )}
                  <td
                    style={{
                      ...cellSt(true, '#1e293b'),
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    {fmt(summaryWage)}
                  </td>
                  {showDeductCol && (() => {
                    const totalDeduct = Number(
                      (totalWage - totalActualWage).toFixed(2),
                    );
                    return (
                      <td
                        style={{
                          ...cellSt(true, totalDeduct > 0 ? '#dc2626' : '#d1d5db'),
                          borderTop: '2px solid #cbd5e1',
                        }}
                      >
                        {totalDeduct > 0 ? `−${fmt(totalDeduct)}` : '—'}
                      </td>
                    );
                  })()}
                  <td
                    style={{
                      ...cellSt(
                        false,
                        totalOtHours > 0 ? '#ea580c' : '#94a3b8',
                      ),
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    {totalOtHours > 0 ? (
                      <>
                        {fmtH(totalOtHours)} ชม.
                        {Math.abs(totalOtEffHours - totalOtHours) > 0.01 && (
                          <span style={{ fontSize: '0.62rem', color: '#9ca3af' }}>
                            {' '}
                            ({totalOtEffHours.toFixed(1)})
                          </span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td
                    style={{
                      ...cellSt(true, summaryOt > 0 ? '#ea580c' : '#94a3b8'),
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    {summaryOt > 0 ? fmt(summaryOt) : '—'}
                  </td>
                  <td
                    style={{
                      ...cellSt(
                        false,
                        totalAllowanceDaily > 0 ? '#0369a1' : '#94a3b8',
                      ),
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    {totalAllowanceDaily > 0 ? fmt(totalAllowanceDaily) : '—'}
                  </td>
                  <td
                    style={{
                      ...cellSt(
                        false,
                        summaryFieldAllowance > 0 ? '#0369a1' : '#94a3b8',
                      ),
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    {summaryFieldAllowance > 0 ? fmt(summaryFieldAllowance) : '—'}
                  </td>
                  {!isWorkTime && (
                    <td
                      style={{
                        ...cellSt(
                          true,
                          summaryLateDed > 0 ? '#f97316' : '#94a3b8',
                        ),
                        borderTop: '2px solid #cbd5e1',
                      }}
                    >
                      {summaryLateDed > 0 ? `−${fmt(summaryLateDed)}` : '—'}
                    </td>
                  )}
                  <td
                    style={{
                      ...cellSt(true, '#065f46', 'right'),
                      borderTop: '2px solid #cbd5e1',
                    }}
                  >
                    {fmt(
                      summaryWage +
                        summaryOt +
                        totalAllowanceDaily +
                        summaryFieldAllowance -
                        (isWorkTime ? 0 : summaryLateDed),
                    )}
                  </td>
                  <td style={{ borderTop: '2px solid #cbd5e1' }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── แบ่งค่าแรงตามโครงการ (พนักงานรายเดือน) ── */}
        {splitRows.length > 0 && (
          <>
            <SectionTitle>🏗️ แบ่งค่าแรงลงโครงการ (ตาม % ชั่วโมง)</SectionTitle>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 6, lineHeight: 1.6 }}>
              ค่าแรง (ชม.จริง) ของเดือน <b>{fmt(projectSplit.fullWage)}</b> (หลังหักวันที่ไม่ได้ค่าแรง/บวกเรตพิเศษ) แบ่งตาม % ชั่วโมงที่ลงแต่ละโครงการ
              (รวม {fmtH(projectSplit.totalHours)} ชม.) — ปกส. / ภาษี / เงินได้อื่นๆ / หัก CRL / หักอื่นๆ ใช้ % เดียวกัน
              · OT / เบี้ยเลี้ยง / ค่าออกสนาม ลงตามโครงการที่ทำจริงในวันนั้น
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 980 }}>
                <thead>
                  <tr>
                    <th style={headSt('left')}>โครงการ</th>
                    <th style={headSt()}>ชม.</th>
                    <th style={headSt()}>%</th>
                    <th style={headSt()}>ค่าแรง</th>
                    <th style={headSt()}>OT</th>
                    <th style={headSt()}>เบี้ยเลี้ยง</th>
                    <th style={headSt()}>ค่าออกสนาม</th>
                    <th style={headSt()}>เงินได้อื่นๆ</th>
                    <th style={headSt()}>ปกส.</th>
                    <th style={headSt()}>ภาษี</th>
                    <th style={headSt()}>หัก CRL</th>
                    <th style={headSt()}>หักอื่นๆ</th>
                    {hasSplitAdjust && <th style={headSt()} title="ส่วนต่างที่ HR แก้ยอดในฟอร์มเงินเดือน + ยอดยกมาจากงวดก่อน">ปรับ/ยกมา</th>}
                    <th style={headSt()}>ได้รับจากโครงการนี้</th>
                  </tr>
                </thead>
                <tbody>
                  {splitRows.map((r) => (
                    <tr key={r.key}>
                      <td style={cellSt(true, '#1e293b', 'left')}>
                        {r.project_number || 'ไม่ระบุโครงการ'}
                        {r.project_name && (
                          <div style={{ fontSize: '0.68rem', fontWeight: 400, color: '#94a3b8' }}>{r.project_name}</div>
                        )}
                      </td>
                      <td style={cellSt(false)}>{fmtH(Math.round(r.hours * 100) / 100)}</td>
                      <td style={cellSt(true, 'rgb(var(--color-primary-500))')}>{(r.share * 100).toFixed(1)}%</td>
                      <td style={cellSt(false)}>
                        {fmt(r.salary)}
                        <div style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                          {fmt(projectSplit.fullWage)} × {(r.share * 100).toFixed(1)}%
                        </div>
                      </td>
                      <td style={cellSt(false, r.ot > 0 ? '#ea580c' : '#cbd5e1')}>{r.ot > 0 ? fmt(r.ot) : '—'}</td>
                      <td style={cellSt(false, r.allowance > 0 ? '#0369a1' : '#cbd5e1')}>{r.allowance > 0 ? fmt(r.allowance) : '—'}</td>
                      <td style={cellSt(false, r.fa > 0 ? '#16a34a' : '#cbd5e1')}>{r.fa > 0 ? fmt(r.fa) : '—'}</td>
                      <td style={cellSt(false, r.bonus > 0 ? '#16a34a' : '#cbd5e1')}>{r.bonus > 0 ? fmt(r.bonus) : '—'}</td>
                      <td style={cellSt(false, r.sso > 0 ? '#64748b' : '#cbd5e1')}>{r.sso > 0 ? `−${fmt(r.sso)}` : '—'}</td>
                      <td style={cellSt(false, r.tax > 0 ? '#64748b' : '#cbd5e1')}>{r.tax > 0 ? `−${fmt(r.tax)}` : '—'}</td>
                      <td style={cellSt(false, r.crl > 0 ? '#b91c1c' : '#cbd5e1')}>{r.crl > 0 ? `−${fmt(r.crl)}` : '—'}</td>
                      <td style={cellSt(false, r.other > 0 ? '#64748b' : '#cbd5e1')}>{r.other > 0 ? `−${fmt(r.other)}` : '—'}</td>
                      {hasSplitAdjust && (
                        <td style={cellSt(false, Math.abs(r.adjust || 0) > 0.005 ? '#7c3aed' : '#cbd5e1')}>
                          {Math.abs(r.adjust || 0) > 0.005 ? `${r.adjust > 0 ? '+' : '−'}${fmt(Math.abs(r.adjust))}` : '—'}
                        </td>
                      )}
                      <td style={cellSt(true, r.payable >= 0 ? '#065f46' : '#ef4444')}>{fmt(r.payable)}</td>
                    </tr>
                  ))}
                  <tr>
                    {[
                      ['left', 'รวม', '#1e293b'],
                      ['right', fmtH(Math.round(splitTotal('hours') * 100) / 100)],
                      ['right', '100%', 'rgb(var(--color-primary-500))'],
                      ['right', fmt(splitTotal('salary'))],
                      ['right', fmt(splitTotal('ot'))],
                      ['right', fmt(splitTotal('allowance'))],
                      ['right', fmt(splitTotal('fa'))],
                      ['right', fmt(splitTotal('bonus'))],
                      ['right', `−${fmt(splitTotal('sso'))}`],
                      ['right', `−${fmt(splitTotal('tax'))}`],
                      ['right', `−${fmt(splitTotal('crl'))}`],
                      ['right', `−${fmt(splitTotal('other'))}`],
                      ...(hasSplitAdjust ? [['right', fmt(splitTotal('adjust')), '#7c3aed']] : []),
                      ['right', fmt(splitTotal('payable')), '#065f46'],
                    ].map(([align, text, color], i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={i} style={{ ...cellSt(true, color, align), borderTop: '2px solid #cbd5e1' }}>{text}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── สรุป — ใช้ค่าจาก form (ตรงกับตาราง) ── */}
        <SectionTitle>💰 สรุป</SectionTitle>
        <div style={{ maxWidth: 360, marginLeft: 'auto' }}>
          {[
            { label: 'ค่าแรง', value: summaryWage, orig: computedWage, overrideKey: null, color: '#1e293b' },
            { label: 'ค่า OT', hours: totalOtHours, value: summaryOt, orig: totalOt, overrideKey: 'overtime', color: summaryOt > 0 ? '#ea580c' : '#94a3b8' },
            { label: 'เบี้ยเลี้ยง', value: allowances, orig: null, overrideKey: null, color: allowances > 0 ? '#0369a1' : '#94a3b8' },
            { label: 'ค่าออกสนาม', value: summaryFieldAllowance, orig: fieldAllowanceTotal, overrideKey: 'field_allowance', color: summaryFieldAllowance > 0 ? '#16a34a' : '#94a3b8' },
            { label: 'เงินได้อื่นๆ (โบนัส)', value: bonus, orig: null, overrideKey: null, color: bonus > 0 ? '#16a34a' : '#94a3b8' },
          ].map(({ label, hours, value, orig, overrideKey, color }) => {
            const isOverridden = overrideKey && manualFields.includes(overrideKey);
            const origDiffers = orig != null && Math.abs(value - orig) > 0.005;
            return (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, alignItems: 'flex-start' }}>
                <Typography variant="body2" color="text.secondary">
                  {label}
                  {hours > 0 && ` (${fmtH(hours)} ชม.)`}
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" fontWeight={500} sx={{ color }}>
                    {fmt(value)}
                  </Typography>
                  {isOverridden && origDiffers && (
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1.3 }}>
                      (ก่อนแก้: {fmt(orig)})
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
          {onResetAll && manualFields.some((f) => ['overtime', 'field_allowance', 'bonus', 'allowances'].includes(f)) && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, mb: 0.25 }}>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => onResetAll({ ot: totalOt, fieldAllowance: fieldAllowanceTotal })}
                sx={{ fontSize: '0.7rem', py: 0.25, px: 1, borderRadius: 1 }}
              >
                ↩ ยกเลิกการแก้ไขทั้งหมด
              </Button>
            </Box>
          )}
          <Divider sx={{ my: 0.75 }} />
          {[
            ...(!isWorkTime ? [{ label: 'หักมาสาย', value: summaryLateDed, color: summaryLateDed > 0 ? '#f97316' : '#94a3b8' }] : []),
            { label: 'หักประกันสังคม', value: ssoDed, color: ssoDed > 0 ? '#64748b' : '#94a3b8' },
            { label: 'หักภาษี', value: taxDed, color: taxDed > 0 ? '#64748b' : '#94a3b8' },
            { label: 'หักอื่นๆ', value: otherDed, color: otherDed > 0 ? '#64748b' : '#94a3b8' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{label}</span>
              <span style={{ fontSize: '0.875rem', fontWeight: 500, color }}>−{fmt(value)}</span>
            </div>
          ))}
          <hr style={{ border: 'none', borderTop: '2px solid #1e293b', margin: '6px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>รวมสุทธิ</span>
            <span style={{ fontWeight: 700, fontSize: '1rem', color: net >= 0 ? '#16a34a' : '#ef4444' }}>{fmt(net)}</span>
          </div>
        </div>
        </div>{/* end content */}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '6px 14px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
            ปิด
          </button>
          <button onClick={handleOpen} style={{ padding: '6px 14px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <PrintIcon fontSize="small" /> ดู PDF
          </button>
          <button onClick={handleDownload} style={{ padding: '6px 14px', border: 'none', background: '#1e293b', color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <DownloadIcon fontSize="small" /> บันทึก PDF
          </button>
        </div>
    </WindowFrame>
  );
}
